import React from "react"
import { Box, IconButton, Typography, Button, Divider } from "@mui/material"
import { Close as CloseIcon, DeleteOutline as DeleteIcon } from "@mui/icons-material"
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined"
import { styled } from "@mui/material/styles"

const StyledSelectionBarButton = styled(Button)({
  backgroundColor: "#FFFFFFCC",
  "&:hover": {
    backgroundColor: "#FFFFFF",
    boxShadow: "0px 0px 15px #00000026"
  }
})

interface DataSourcesSelectionBarProps {
  selectedCount: number
  successMessage: string | null
  onClose: () => void
  onApprove: () => void
  onDelete: () => void
  canModify: boolean
  allSelectedArePending: boolean
  hasDeletableRecords: boolean
}

const DataSourcesSelectionBar: React.FC<DataSourcesSelectionBarProps> = ({
  selectedCount,
  successMessage,
  onClose,
  onApprove,
  onDelete,
  canModify,
  allSelectedArePending,
  hasDeletableRecords
}) => {
  if (selectedCount === 0 && !successMessage) {
    return null
  }

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 74,
        left: "50%",
        transform: "translateX(-50%)",
        backgroundColor: "#CBF5FF",
        borderRadius: { xs: "16px", md: "100px" },
        padding: { xs: "12px 16px", md: "0px 24px" },
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "stretch", md: "center" },
        gap: { xs: 1, md: 2 },
        boxShadow: "-3px 3px 25px #00000026",
        zIndex: 1000,
        color: "#01294E"
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            color: "#01294E",
            py: { xs: 0, md: "12px" },
            "&:hover": {
              backgroundColor: "rgba(1, 41, 78, 0.08)"
            }
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography variant="body2" sx={{ fontWeight: 500, color: "#01294E" }}>
          {successMessage
            ? successMessage
            : `${selectedCount} ${selectedCount === 1 ? "source" : "sources"} selected`}
        </Typography>
      </Box>
      {!successMessage && canModify && (allSelectedArePending || hasDeletableRecords) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {allSelectedArePending && (
            <StyledSelectionBarButton
              variant="outlined"
              size="small"
              onClick={onApprove}
              startIcon={<CheckOutlinedIcon sx={{ color: "#00D2FF" }} />}
              sx={{ flex: { xs: 1, md: "auto" } }}
            >
              Approve
            </StyledSelectionBarButton>
          )}
          {allSelectedArePending && hasDeletableRecords && (
            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: "#01294E", opacity: 0.3, display: { xs: "none", md: "block" } }}
            />
          )}
          {hasDeletableRecords && (
            <StyledSelectionBarButton
              variant="outlined"
              size="small"
              startIcon={<DeleteIcon sx={{ color: "#00D2FF" }} />}
              onClick={onDelete}
              sx={{ flex: { xs: 1, md: "auto" } }}
            >
              Delete
            </StyledSelectionBarButton>
          )}
        </Box>
      )}
    </Box>
  )
}

export default DataSourcesSelectionBar
